/**
 * SettingsService
 *
 * A reusable, object-oriented service for managing user preferences and settings.
 * Follows Service-Oriented Design patterns for modularity and reusability.
 *
 * Features:
 * - Persistent storage using localStorage
 * - Default values for all settings
 * - Type-safe getters and setters
 * - Event-based notification of changes
 */

type SettingValue = string | number | boolean;
type SettingListener = (newValue: SettingValue, oldValue: SettingValue | undefined) => void;

interface SettingsData {
    dataDisplayRows: number;
    theme: string;
    aiBackend: string;  // 'claude', 'llm', or 'local'
    aiModel: string;    // active model ID, e.g. 'claude-sonnet-4-20250514'
    [key: string]: SettingValue;
}

/**
 * SettingsService class for managing application settings
 */
export class SettingsService {
    private storageKey: string;
    private defaults: SettingsData;
    private settings: SettingsData;
    private listeners: Map<string, SettingListener[]>;

    /**
     * Create a SettingsService instance
     * @param defaults - Default settings values
     */
    constructor(defaults: Partial<SettingsData> = {}) {
        this.storageKey = 'asd_user_settings';
        this.defaults = {
            dataDisplayRows: 20,
            theme: 'light',
            aiBackend: 'claude',  // Default to Claude AI
            aiModel: 'claude-sonnet-4-20250514',  // Default model
            ...defaults
        };
        this.settings = this.loadSettings();
        this.listeners = new Map<string, SettingListener[]>();
    }

    /**
     * Load settings from localStorage, merging with defaults
     * @returns Merged settings
     */
    private loadSettings(): SettingsData {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                return { ...this.defaults, ...parsed };
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
        }
        return { ...this.defaults };
    }

    /**
     * Save settings to localStorage
     */
    private saveSettings(): void {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(this.settings));
            console.log('Settings saved:', this.settings);
        } catch (error) {
            console.error('Failed to save settings:', error);
        }
    }

    /**
     * Get a setting value
     * @param key - Setting key
     * @returns Setting value
     */
    public get<K extends keyof SettingsData>(key: K): SettingsData[K];
    public get(key: string): SettingValue | undefined;
    public get(key: string): SettingValue | undefined {
        return this.settings[key] !== undefined ? this.settings[key] : this.defaults[key];
    }

    /**
     * Set a setting value
     * @param key - Setting key
     * @param value - Setting value
     */
    public set<K extends keyof SettingsData>(key: K, value: SettingsData[K]): void;
    public set(key: string, value: SettingValue): void;
    public set(key: string, value: SettingValue): void {
        const oldValue = this.settings[key];
        this.settings[key] = value;
        this.saveSettings();

        // Notify listeners
        this.notifyListeners(key, value, oldValue);

        console.log(`Setting updated: ${key} = ${value}`);
    }

    /**
     * Get the data display rows setting
     * @returns Number of rows to display (20, 50, or 100)
     */
    public getDataDisplayRows(): number {
        const value = this.get('dataDisplayRows');
        // Validate and constrain to allowed values
        if (typeof value === 'number' && [20, 50, 100].includes(value)) {
            return value;
        }
        return 20; // Default fallback
    }

    /**
     * Set the data display rows setting
     * @param rows - Number of rows (20, 50, or 100)
     */
    public setDataDisplayRows(rows: number): void {
        // Validate input
        if (![20, 50, 100].includes(rows)) {
            console.warn(`Invalid data display rows value: ${rows}. Must be 20, 50, or 100.`);
            return;
        }
        this.set('dataDisplayRows', rows);
    }

    /**
     * Get the AI backend setting
     * @returns 'claude' for Real Claude AI, 'llm' for Self-Hosted LLM, or 'local' for local simulation
     */
    public getAiBackend(): string {
        const value = this.get('aiBackend');
        // Validate and constrain to allowed values
        if (typeof value === 'string' && ['claude', 'llm', 'local'].includes(value)) {
            return value;
        }
        return 'claude'; // Default fallback to Claude AI
    }

    /**
     * Set the AI backend setting
     * @param backend - 'claude', 'llm', or 'local'
     */
    public setAiBackend(backend: string): void {
        // Validate input
        if (!['claude', 'llm', 'local'].includes(backend)) {
            console.warn(`Invalid AI backend value: ${backend}. Must be 'claude', 'llm', or 'local'.`);
            return;
        }
        this.set('aiBackend', backend);
    }

    /**
     * Check if Claude AI is enabled
     * @returns true if Claude AI is enabled
     */
    public isClaudeEnabled(): boolean {
        return this.getAiBackend() === 'claude';
    }

    /**
     * Check if Self-Hosted LLM is enabled
     * @returns true if Self-Hosted LLM is enabled
     */
    public isLLMEnabled(): boolean {
        return this.getAiBackend() === 'llm';
    }

    /**
     * Get the active AI model ID
     * @returns model ID string, e.g. 'claude-sonnet-4-20250514'
     */
    public getAiModel(): string {
        const value = this.get('aiModel');
        return typeof value === 'string' && value.length > 0
            ? value
            : 'claude-sonnet-4-20250514';
    }

    /**
     * Set the active AI model and auto-derive the aiBackend.
     * Models starting with 'claude-' map to 'claude', all others to 'llm'.
     * @param modelId - model identifier
     */
    public setAiModel(modelId: string): void {
        this.set('aiModel', modelId);
        // Auto-derive backend from model ID
        const backend = modelId.startsWith('claude-') ? 'claude' : 'llm';
        this.set('aiBackend', backend);
    }

    /**
     * Register a listener for setting changes
     * @param key - Setting key to listen for
     * @param callback - Callback function (newValue, oldValue) => void
     */
    public onChange(key: string, callback: SettingListener): void {
        if (!this.listeners.has(key)) {
            this.listeners.set(key, []);
        }
        this.listeners.get(key)?.push(callback);
    }

    /**
     * Notify all listeners for a setting change
     * @param key - Setting key
     * @param newValue - New value
     * @param oldValue - Old value
     */
    private notifyListeners(key: string, newValue: SettingValue, oldValue: SettingValue | undefined): void {
        const keyListeners = this.listeners.get(key);
        if (keyListeners) {
            keyListeners.forEach(callback => {
                try {
                    callback(newValue, oldValue);
                } catch (error) {
                    console.error(`Error in setting change listener for ${key}:`, error);
                }
            });
        }
    }

    /**
     * Reset all settings to defaults
     */
    public reset(): void {
        this.settings = { ...this.defaults };
        this.saveSettings();
        console.log('Settings reset to defaults');
    }

    /**
     * Export settings as JSON
     * @returns JSON string
     */
    public export(): string {
        return JSON.stringify(this.settings, null, 2);
    }

    /**
     * Import settings from JSON
     * @param json - JSON string
     */
    public import(json: string): void {
        try {
            const imported = JSON.parse(json);
            this.settings = { ...this.defaults, ...imported };
            this.saveSettings();
            console.log('Settings imported successfully');
        } catch (error) {
            console.error('Failed to import settings:', error);
        }
    }

    /**
     * Get all settings
     * @returns All settings data
     */
    public getAll(): SettingsData {
        return { ...this.settings };
    }

    /**
     * Remove a specific listener
     * @param key - Setting key
     * @param callback - Callback to remove
     */
    public removeListener(key: string, callback: SettingListener): void {
        const keyListeners = this.listeners.get(key);
        if (keyListeners) {
            const index = keyListeners.indexOf(callback);
            if (index !== -1) {
                keyListeners.splice(index, 1);
            }
        }
    }

    /**
     * Clear all listeners for a key
     * @param key - Setting key
     */
    public clearListeners(key?: string): void {
        if (key) {
            this.listeners.delete(key);
        } else {
            this.listeners.clear();
        }
    }
}

// Export to global scope
declare global {
    interface Window {
        SettingsService: typeof SettingsService;
    }
}

if (typeof window !== 'undefined') {
    window.SettingsService = SettingsService;
}

console.log('SettingsService loaded');

